import React from 'react';
import { ArchiveIcon, BellIcon, CalendarIcon, HomeIcon, MailIcon, SearchIcon, SettingsIcon, UsersIcon } from 'lucide-react';
import { InitialAvatar } from '../InitialAvatar';
import { CURRENT_MEMBER } from '../../data/community';
export type PortalSection = 'home' | 'people' | 'events' | 'archive' | 'voices' | 'profile';
const NAV: {
  key: PortalSection;
  label: string;
  icon: typeof HomeIcon;
  hint: string;
}[] = [{
  key: 'home',
  label: 'Home',
  icon: HomeIcon,
  hint: 'Where things stand'
}, {
  key: 'people',
  label: 'People',
  icon: UsersIcon,
  hint: '28 members'
}, {
  key: 'events',
  label: 'Events',
  icon: CalendarIcon,
  hint: '4 coming up'
}, {
  key: 'archive',
  label: 'Archive',
  icon: ArchiveIcon,
  hint: 'Photographs, recordings, memories'
}, {
  key: 'voices',
  label: 'Voices',
  icon: MailIcon,
  hint: 'This month’s letter'
}];
interface AppShellProps {
  section: PortalSection;
  onNavigate: (section: PortalSection) => void;
  onSignOut: () => void;
  children: React.ReactNode;
}
export function AppShell({
  section,
  onNavigate,
  onSignOut,
  children
}: AppShellProps) {
  return <div className="min-h-screen w-full bg-canvas lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]" data-id="element-203" data-mp="2gd4tl1cg57as">
      <aside className="hidden border-r border-rule bg-card lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col" data-id="element-204" data-mp="22lq88nv2gu0k">
        <div className="flex flex-col gap-0.5 border-b border-rule px-5 py-4" data-id="element-205" data-mp="26m0gk76bzwil">
          <span className="text-heading font-semibold tracking-tight text-ink" data-id="element-206" data-mp="1snadmawtz2mb">
            We Are Stepping Stone
          </span>
          <span className="text-eyebrow-caption font-medium uppercase tracking-wide text-ink-muted" data-id="element-207" data-mp="1xksfxigcejlc">
            Run by alumni volunteers
          </span>
        </div>

        <nav aria-label="Portal" className="flex flex-1 flex-col gap-0.5 p-3" data-id="element-208" data-mp="1nphc3v6h2wsd">
          {NAV.map(item => {
          const active = item.key === section;
          return <button key={item.key} type="button" onClick={() => onNavigate(item.key)} aria-current={active ? 'page' : undefined} className={`flex min-h-tap flex-col justify-center gap-0.5 rounded-field px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${active ? 'bg-primary-soft' : 'hover:bg-canvas'}`} data-id="element-209" data-mp="3i1ytwn8jl4pm">
                <span className={`flex items-center gap-2.5 text-body ${active ? 'font-semibold text-primary-text' : 'font-medium text-ink'}`} data-id="element-210" data-mp="1dk8ybkbxb5v4">
                  <item.icon aria-hidden="true" className="h-4 w-4" data-id="element-211" data-mp="14qdviiy9xrin" />
                  {item.label}
                </span>
                <span className="pl-[1.625rem] text-small text-ink-muted" data-id="element-212" data-mp="1x9jfl2rbhyvc">{item.hint}</span>
              </button>;
        })}
        </nav>

        <div className="flex flex-col gap-1 border-t border-rule p-3" data-id="element-213" data-mp="1sh04ccg3rftp">
          <button type="button" onClick={() => onNavigate('profile')} aria-current={section === 'profile' ? 'page' : undefined} className={`flex items-center gap-3 rounded-field px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${section === 'profile' ? 'bg-primary-soft' : 'hover:bg-canvas'}`} data-id="element-214" data-mp="1zuhfimdfxp2p">
            <InitialAvatar name={CURRENT_MEMBER.fullName} size={36} data-id="element-215" data-mp="2nqv7crxv8w98" />
            <span className="flex min-w-0 flex-col" data-id="element-216" data-mp="2np21hev2ug2a">
              <span className="truncate text-small font-medium text-ink" data-id="element-217" data-mp="2sj6bk6zn347l">
                {CURRENT_MEMBER.publicName}
              </span>
              <span className="text-small text-ink-muted" data-id="element-218" data-mp="2xjat80caa4ze">Your profile</span>
            </span>
          </button>
          <button type="button" onClick={() => onNavigate('profile')} className="flex min-h-tap items-center gap-2.5 rounded-field px-3 text-left text-small text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-219" data-mp="36hw5jdr5wnrq">
            <SettingsIcon aria-hidden="true" className="h-4 w-4" data-id="element-220" data-mp="32kgzo5ge5p5n" />
            Settings and visibility
          </button>
          <button type="button" onClick={onSignOut} className="flex min-h-tap items-center gap-2.5 rounded-field px-3 text-left text-small text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-221" data-mp="2xnyxndcg44x0">
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col" data-id="element-222" data-mp="2exh9lm2k5xhz">
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-rule bg-card px-4 py-2.5 sm:px-6" data-id="element-223" data-mp="3v8uxol7hclh7">
          <span className="text-heading font-semibold tracking-tight text-ink lg:hidden" data-id="element-224" data-mp="3q9rtt438klat">
            Portal
          </span>

          <label className="relative hidden min-w-0 flex-1 items-center lg:flex" data-id="element-225" data-mp="1c4wlbzf4hp7s">
            <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3 h-4 w-4 text-ink-muted" data-id="element-226" data-mp="1h3g8u49d7929" />
            <span className="sr-only" data-id="element-227" data-mp="180othtddbt5c">Search people, years, and the archive</span>
            <input type="search" placeholder="Search people, years, the archive…" className="min-h-tap w-full max-w-md rounded-pill border border-rule bg-canvas pl-9 pr-4 text-small text-ink placeholder:text-ink-subtle focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30" data-id="element-228" data-mp="1vqrhnye5weyo" />
          </label>

          <div className="ml-auto flex items-center gap-2" data-id="element-229" data-mp="2epnab5adglae">
            <button type="button" className="relative inline-flex h-11 w-11 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-230" data-mp="2eoca1fg4xvo1">
              <span className="sr-only" data-id="element-231" data-mp="2ob6h39ayrb79">Acknowledgements</span>
              <BellIcon aria-hidden="true" className="h-5 w-5" data-id="element-232" data-mp="2jd4zkst6rjxw" />
              <span aria-hidden="true" className="absolute right-2.5 top-2.5 h-2 w-2 rounded-pill bg-accent" data-id="element-233" data-mp="2efbtdzrvupxm" />
            </button>
            <span className="lg:hidden" data-id="element-234" data-mp="2afks324qcreu">
              <InitialAvatar name={CURRENT_MEMBER.fullName} size={36} data-id="element-235" data-mp="25mhyitzydhyn" />
            </span>
          </div>
        </div>

        <nav aria-label="Portal sections" className="flex gap-1 overflow-x-auto border-b border-rule bg-card px-3 py-2 lg:hidden" data-id="element-236" data-mp="20ogg8bacpmo8">
          {NAV.map(item => <button key={item.key} type="button" onClick={() => onNavigate(item.key)} aria-current={item.key === section ? 'page' : undefined} className={`inline-flex min-h-tap shrink-0 items-center gap-2 rounded-pill px-3.5 text-small ${item.key === section ? 'bg-primary-soft font-medium text-primary-text' : 'text-ink-muted'}`} data-id="element-237" data-mp="1qofu6hl3ksgc">
              <item.icon aria-hidden="true" className="h-4 w-4" data-id="element-238" data-mp="g5w6asvzllfs" />
              {item.label}
            </button>)}
        </nav>

        <main className="flex-1" data-id="element-239" data-mp="3ep352rtaglz1">{children}</main>
      </div>
    </div>;
}